// ══════════════════════════════════════════════════════════════════════════════
// FICHE CLIENT COMPLÈTE - CRM
// Timeline, devis, factures, échanges (emails, messages, appels, notes)
// + Appels WebRTC avec enregistrement et résumé IA
// + Chantiers et Comptes Rendus interconnectés
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Phone, Mail, MapPin, Building, FileText, Receipt, 
  MessageSquare, StickyNote, PhoneCall, CheckCircle, Clock,
  Send, Plus, ChevronDown, ExternalLink, Copy, Euro,
  User, Calendar, TrendingUp, AlertCircle, Mic, MicOff,
  Video, VideoOff, PhoneOff, Loader2, Sparkles, FileAudio,
  RefreshCw, MessageCircle, UserCheck, HardHat, ClipboardCheck,
  Camera, ChevronRight, Brain, Shield, AlertTriangle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes pour la timeline
const TIMELINE_ICONS = {
  devis: FileText,
  facture: Receipt,
  email: Mail,
  message: MessageSquare,
  note: StickyNote,
  call: PhoneCall,
  signature: CheckCircle,
  chantier: HardHat,
  compte_rendu: ClipboardCheck,
  pointage: Clock,
};

// Couleurs pour les types
const TIMELINE_COLORS = {
  devis: 'bg-orange-100 text-orange-600 border-orange-200',
  facture: 'bg-green-100 text-green-600 border-green-200',
  email: 'bg-blue-100 text-blue-600 border-blue-200',
  message: 'bg-purple-100 text-purple-600 border-purple-200',
  note: 'bg-amber-100 text-amber-600 border-amber-200',
  call: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  signature: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  chantier: 'bg-violet-100 text-violet-600 border-violet-200',
  compte_rendu: 'bg-orange-100 text-orange-600 border-orange-200',
  pointage: 'bg-teal-100 text-teal-600 border-teal-200',
};

export default function ClientFileModal({ clientId, isOpen, onClose }) {
  const navigate = useNavigate();
  const [clientFile, setClientFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showAddExchange, setShowAddExchange] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);
  const [newExchange, setNewExchange] = useState({
    type: 'note',
    subject: '',
    content: '',
    direction: 'outgoing'
  });
  
  // ══════════════════════════════════════════════════════════════
  // ÉTAT CHANTIERS + IA
  // ══════════════════════════════════════════════════════════════
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAiSummary, setLoadingAiSummary] = useState(false);
  const [expandedChantier, setExpandedChantier] = useState(null);
  const [showCompteRenduDetail, setShowCompteRenduDetail] = useState(null);
  
  // ══════════════════════════════════════════════════════════════
  // ÉTAT APPEL + ENREGISTREMENT
  // ══════════════════════════════════════════════════════════════
  const [showCallModal, setShowCallModal] = useState(false);
  const [showLegalWarning, setShowLegalWarning] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle, calling, recording, processing, done
  const [isRecording, setIsRecording] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callId, setCallId] = useState(null);
  const [callSummary, setCallSummary] = useState(null);
  
  // Refs pour l'enregistrement audio
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const callTimerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchClientFile();
      setAiSummary(null);
    }
  }, [isOpen, clientId]);
  
  // ══════════════════════════════════════════════════════════════
  // LOGIQUE D'APPEL + ENREGISTREMENT
  // ══════════════════════════════════════════════════════════════
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);
  
  const startCall = async () => {
    // Afficher d'abord l'avertissement légal
    setShowLegalWarning(true);
  };
  
  const confirmAndStartCall = async () => {
    // Fermer l'avertissement et lancer l'appel
    setShowLegalWarning(false);
    
    try {
      setShowCallModal(true);
      setCallState('calling');
      setCallDuration(0);
      setCallSummary(null);
      audioChunksRef.current = [];
      
      // Générer un call_id
      const newCallId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setCallId(newCallId);
      
      // Notifier le backend
      const token = localStorage.getItem('client_token');
      await fetch(`${API_URL}/api/calls/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: clientId,
          client_name: clientFile?.name || 'Client',
          direction: 'outgoing'
        })
      });
      
      // Simuler la connexion
      setTimeout(() => {
        setCallState('recording');
        startRecording();
        
        // Timer de durée
        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      }, 1500);
      
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error('Erreur lors du démarrage de l\'appel');
      setShowCallModal(false);
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.start(1000); // Chunk toutes les secondes
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };
  
  const endCall = async (withSummary = true) => {
    try {
      // Arrêter le timer
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      
      // Arrêter l'enregistrement
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      // Arrêter le stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      setIsRecording(false);
      
      if (withSummary && audioChunksRef.current.length > 0) {
        setCallState('processing');
        
        // Créer le blob audio
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Envoyer pour transcription + résumé
        const formData = new FormData();
        formData.append('audio_file', audioBlob, 'call_recording.webm');
        formData.append('call_id', callId);
        formData.append('client_id', clientId);
        formData.append('duration_seconds', callDuration.toString());
        
        const token = localStorage.getItem('client_token');
        const response = await fetch(`${API_URL}/api/calls/summarize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setCallSummary(result);
          setCallState('done');
          toast.success('Compte-rendu généré avec succès !');
          fetchClientFile(); // Rafraîchir la timeline
        } else {
          throw new Error('Erreur lors du traitement');
        }
      } else {
        // Juste enregistrer l'appel sans résumé
        const token = localStorage.getItem('client_token');
        await fetch(`${API_URL}/api/calls/end`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            call_id: callId,
            duration_seconds: callDuration
          })
        });
        
        setCallState('done');
        toast.success('Appel enregistré dans la timeline');
        fetchClientFile();
      }
      
    } catch (error) {
      console.error('Error ending call:', error);
      toast.error('Erreur lors de la fin de l\'appel');
      setCallState('idle');
      setShowCallModal(false);
    }
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ══════════════════════════════════════════════════════════════
  // SYNCHRONISATION EMAILS
  // ══════════════════════════════════════════════════════════════
  
  const syncEmails = async () => {
    setSyncingEmails(true);
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}/sync-emails`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.synced > 0) {
          toast.success(`${data.synced} email(s) synchronisé(s) !`);
          fetchClientFile(); // Rafraîchir la timeline
        } else {
          toast.info('Aucun nouvel email à synchroniser');
        }
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Error syncing emails:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSyncingEmails(false);
    }
  };
  
  // ══════════════════════════════════════════════════════════════
  // OUVRIR ABRACADATCHAT
  // ══════════════════════════════════════════════════════════════
  
  const openAbracadatchat = async () => {
    const token = localStorage.getItem('client_token');
    
    try {
      // Appeler l'API pour créer/ouvrir la conversation
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}/open-chat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Fermer le modal et naviguer vers la messagerie avec la conversation ouverte
        onClose();
        navigate('/cockpit-pro/messagerie', { 
          state: { 
            openConversationId: data.conversation_id,
            clientName: data.client_name
          } 
        });
      } else {
        toast.error('Erreur lors de l\'ouverture de la conversation');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      toast.error('Erreur de connexion');
    }
  };
  
  // ══════════════════════════════════════════════════════════════
  // CONVERSION PROSPECT → CLIENT
  // ══════════════════════════════════════════════════════════════
  
  const convertToClient = async () => {
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}/convert-to-client`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Mettre à jour localement
        setClientFile(prev => ({ ...prev, status: 'client' }));
        fetchClientFile(); // Rafraîchir toutes les données
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la conversion');
      }
    } catch (error) {
      console.error('Error converting:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Données démo pour le mode sans authentification
  const DEMO_CLIENT_FILE = {
    id: clientId,
    name: 'Client Démo',
    phone: '06 11 22 33 44',
    email: 'demo@email.fr',
    address: '15 rue de la Paix',
    city: 'Paris',
    postal_code: '75001',
    status: 'prospect',
    devis: [],
    invoices: [],
    exchanges: [
      { id: 'ex1', type: 'note', subject: 'Premier contact', content: 'Demande de devis pour rénovation cuisine', direction: 'incoming', created_at: new Date().toISOString(), created_by_name: 'Client' },
    ],
    stats: { total_devis: 0, total_invoiced: 0 },
    created_at: new Date().toISOString()
  };
  
  const fetchClientFile = async () => {
    setLoading(true);
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClientFile(data);
      } else {
        // Mode démo - utiliser données fictives
        console.log('Mode démo activé pour la fiche client');
        setClientFile(DEMO_CLIENT_FILE);
      }
    } catch (error) {
      console.error('Error:', error);
      // Mode démo - utiliser données fictives
      setClientFile(DEMO_CLIENT_FILE);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExchange = async () => {
    if (!newExchange.content.trim()) {
      toast.error('Le contenu est requis');
      return;
    }

    const token = localStorage.getItem('client_token');
    setSendingMessage(true);

    try {
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}/exchange`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: clientId,
          type: newExchange.type,
          subject: newExchange.subject,
          content: newExchange.content,
          direction: newExchange.direction
        })
      });

      if (response.ok) {
        toast.success('Échange ajouté');
        setNewExchange({ type: 'note', subject: '', content: '', direction: 'outgoing' });
        setShowAddExchange(false);
        fetchClientFile(); // Rafraîchir
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };
  
  // ══════════════════════════════════════════════════════════════
  // FONCTIONS CHANTIERS + IA
  // ══════════════════════════════════════════════════════════════
  
  const generateAiSummary = async () => {
    setLoadingAiSummary(true);
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/crm/client/${clientId}/ai-summary`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiSummary(data.summary);
        toast.success('Résumé IA généré');
      } else {
        toast.error('Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoadingAiSummary(false);
    }
  };
  
  const navigateToCompteRendu = (chantierId) => {
    onClose();
    navigate(`/espace-client/crm-batiment/chantiers/compte-rendu?chantier_id=${chantierId}`);
  };
  
  const getStageLabel = (stage) => {
    const labels = {
      preparation: 'Préparation',
      en_cours: 'En cours',
      finitions: 'Finitions',
      termine: 'Terminé'
    };
    return labels[stage] || stage;
  };
  
  const getStageColor = (stage) => {
    const colors = {
      preparation: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-amber-100 text-amber-700',
      finitions: 'bg-purple-100 text-purple-700',
      termine: 'bg-green-100 text-green-700'
    };
    return colors[stage] || 'bg-slate-100 text-slate-700';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-4 border-[#1E3A5F] border-t-transparent rounded-full" />
            </div>
          ) : clientFile ? (
            <>
              {/* Header */}
              <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <User size={28} className="text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-white">{clientFile.name}</h2>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          clientFile.is_client 
                            ? 'bg-green-400 text-green-900' 
                            : 'bg-amber-400 text-amber-900'
                        }`}>
                          {clientFile.is_client ? 'Client' : 'Prospect'}
                        </span>
                      </div>
                      {clientFile.company_name && (
                        <p className="text-white/70 text-sm">{clientFile.company_name}</p>
                      )}
                      <p className="text-white/60 text-xs mt-1">
                        Premier contact: {formatDate(clientFile.first_contact_at)}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Stats rapides */}
                <div className="grid grid-cols-5 gap-3 mt-4">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{clientFile.total_devis}</p>
                    <p className="text-white/70 text-xs">Devis</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{clientFile.total_factures}</p>
                    <p className="text-white/70 text-xs">Factures</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{formatCurrency(clientFile.total_revenue)}</p>
                    <p className="text-white/70 text-xs">CA total</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-violet-300">{clientFile.total_chantiers || 0}</p>
                    <p className="text-white/70 text-xs">Chantiers</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-300">{(clientFile.total_heures_chantier || 0).toFixed(1)}h</p>
                    <p className="text-white/70 text-xs">Heures</p>
                  </div>
                </div>
              </div>

              {/* Contacts rapides */}
              <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap gap-2">
                {clientFile.phone && (
                  <>
                    <a href={`tel:${clientFile.phone}`} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm">
                      <Phone size={14} className="text-[#1E3A5F]" />
                      {clientFile.phone}
                    </a>
                    <Button 
                      size="sm" 
                      onClick={startCall}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                      data-testid="call-client-btn"
                    >
                      <PhoneCall size={14} className="mr-1" />
                      Appeler + Résumé IA
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={openAbracadatchat}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      data-testid="abracadatchat-btn"
                    >
                      <MessageCircle size={14} className="mr-1" />
                      Abracadatchat
                    </Button>
                  </>
                )}
                {clientFile.email && (
                  <>
                    <a href={`mailto:${clientFile.email}`} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm">
                      <Mail size={14} className="text-[#1E3A5F]" />
                      {clientFile.email}
                    </a>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={syncEmails}
                      disabled={syncingEmails}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      data-testid="sync-emails-btn"
                    >
                      <RefreshCw size={14} className={`mr-1 ${syncingEmails ? 'animate-spin' : ''}`} />
                      {syncingEmails ? 'Sync...' : 'Sync Emails'}
                    </Button>
                  </>
                )}
                {clientFile.address && (
                  <span className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-600">
                    <MapPin size={14} />
                    {clientFile.address}, {clientFile.postal_code} {clientFile.city}
                  </span>
                )}
                
                {/* Bouton Convertir en Client - visible seulement si c'est un prospect */}
                {clientFile.status !== 'client' && (
                  <Button 
                    size="sm" 
                    onClick={convertToClient}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    data-testid="convert-to-client-btn"
                  >
                    <UserCheck size={14} className="mr-1" />
                    Convertir en Client
                  </Button>
                )}
              </div>

              {/* Onglets */}
              <div className="px-6 py-2 border-b border-slate-100 flex gap-2 overflow-x-auto">
                {['timeline', 'chantiers', 'devis', 'factures', 'echanges'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-1 ${
                      activeTab === tab
                        ? 'bg-[#1E3A5F] text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {tab === 'timeline' && 'Historique'}
                    {tab === 'chantiers' && (
                      <>
                        <HardHat size={14} />
                        Chantiers ({clientFile.chantiers?.length || 0})
                      </>
                    )}
                    {tab === 'devis' && `Devis (${clientFile.devis?.length || 0})`}
                    {tab === 'factures' && `Factures (${clientFile.factures?.length || 0})`}
                    {tab === 'echanges' && 'Échanges'}
                  </button>
                ))}
                
                {/* Bouton Résumé IA */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generateAiSummary}
                  disabled={loadingAiSummary}
                  className="ml-auto border-purple-300 text-purple-600 hover:bg-purple-50"
                  data-testid="ai-summary-btn"
                >
                  {loadingAiSummary ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <Brain size={14} className="mr-1" />
                  )}
                  Résumé IA
                </Button>
              </div>
              
              {/* Résumé IA (si généré) */}
              {aiSummary && (
                <div className="px-6 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
                  <div className="flex items-start gap-2">
                    <Sparkles size={18} className="text-purple-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-purple-600 mb-1">Analyse IA</p>
                      <p className="text-sm text-slate-700 whitespace-pre-line">{aiSummary}</p>
                    </div>
                    <button 
                      onClick={() => setAiSummary(null)}
                      className="p-1 hover:bg-purple-100 rounded"
                    >
                      <X size={14} className="text-purple-400" />
                    </button>
                  </div>
                </div>
              )}

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Timeline */}
                {activeTab === 'timeline' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800">Historique complet</h3>
                      <Button size="sm" onClick={() => setShowAddExchange(true)}>
                        <Plus size={14} className="mr-1" /> Ajouter
                      </Button>
                    </div>

                    <div className="relative">
                      {/* Ligne verticale */}
                      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />

                      {clientFile.timeline?.map((event, index) => {
                        const Icon = TIMELINE_ICONS[event.type] || Clock;
                        const colorClass = TIMELINE_COLORS[event.type] || 'bg-slate-100 text-slate-600';

                        return (
                          <motion.div
                            key={event.id || index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="relative flex gap-4 pb-4"
                          >
                            {/* Icône */}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 bg-white z-10 ${colorClass}`}>
                              <Icon size={18} />
                            </div>

                            {/* Contenu */}
                            <div className="flex-1 bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-slate-800">{event.title}</p>
                                  {event.description && (
                                    <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                                  )}
                                </div>
                                {event.amount && (
                                  <span className="font-bold text-[#1E3A5F]">{formatCurrency(event.amount)}</span>
                                )}
                              </div>

                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                <span>{formatDateTime(event.date)}</span>
                                {event.status && (
                                  <span className={`px-2 py-0.5 rounded-full ${
                                    event.status === 'signed' || event.status === 'paid' 
                                      ? 'bg-green-100 text-green-600'
                                      : event.status === 'sent'
                                      ? 'bg-blue-100 text-blue-600'
                                      : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {event.status === 'sent' ? 'Envoyé' : 
                                     event.status === 'signed' ? 'Signé' :
                                     event.status === 'paid' ? 'Payé' :
                                     event.status === 'draft' ? 'Brouillon' : event.status}
                                  </span>
                                )}
                                {event.link && (
                                  <a href={event.link} className="text-[#F97316] hover:underline flex items-center gap-1">
                                    Voir <ExternalLink size={12} />
                                  </a>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}

                      {(!clientFile.timeline || clientFile.timeline.length === 0) && (
                        <p className="text-center text-slate-400 py-8">Aucun historique</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Devis */}
                {activeTab === 'devis' && (
                  <div className="space-y-3">
                    {clientFile.devis?.map((devis) => (
                      <div key={devis.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-3">
                          <FileText className="text-[#F97316]" />
                          <div>
                            <p className="font-medium">{devis.devis_number}</p>
                            <p className="text-sm text-slate-500">{devis.title || devis.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(devis.total)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            devis.signed ? 'bg-green-100 text-green-600' : 
                            devis.status === 'sent' ? 'bg-blue-100 text-blue-600' : 
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {devis.signed ? 'Signé' : devis.status === 'sent' ? 'Envoyé' : 'Brouillon'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {(!clientFile.devis || clientFile.devis.length === 0) && (
                      <p className="text-center text-slate-400 py-8">Aucun devis</p>
                    )}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* ONGLET CHANTIERS + COMPTES RENDUS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'chantiers' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <HardHat size={18} className="text-violet-600" />
                        Chantiers et Comptes Rendus
                      </h3>
                    </div>

                    {clientFile.chantiers?.length > 0 ? (
                      <div className="space-y-4">
                        {clientFile.chantiers.map((chantier) => (
                          <div 
                            key={chantier.id}
                            className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                          >
                            {/* En-tête du chantier */}
                            <div 
                              className="p-4 cursor-pointer"
                              onClick={() => setExpandedChantier(expandedChantier === chantier.id ? null : chantier.id)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                                    <HardHat size={20} className="text-violet-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">{chantier.address || 'Adresse non définie'}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStageColor(chantier.pipeline_stage)}`}>
                                        {getStageLabel(chantier.pipeline_stage)}
                                      </span>
                                      {chantier.latitude && chantier.longitude && (
                                        <span className="text-xs text-green-600 flex items-center gap-1">
                                          <MapPin size={10} /> GPS
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right flex items-center gap-3">
                                  <div>
                                    <p className="text-lg font-bold text-violet-600">{(chantier.total_hours || 0).toFixed(1)}h</p>
                                    <p className="text-xs text-slate-500">{chantier.compte_rendus_count || clientFile.comptes_rendus?.filter(cr => cr.chantier_id === chantier.id).length || 0} CR</p>
                                  </div>
                                  <ChevronRight 
                                    size={20} 
                                    className={`text-slate-400 transition-transform ${expandedChantier === chantier.id ? 'rotate-90' : ''}`} 
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Détails étendus */}
                            <AnimatePresence>
                              {expandedChantier === chantier.id && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="border-t border-slate-100"
                                >
                                  <div className="p-4 bg-slate-50">
                                    {/* Bouton créer compte rendu */}
                                    <Button
                                      size="sm"
                                      onClick={() => navigateToCompteRendu(chantier.id)}
                                      className="mb-4 bg-orange-500 hover:bg-orange-600 text-white"
                                      data-testid={`create-cr-${chantier.id}`}
                                    >
                                      <ClipboardCheck size={14} className="mr-1" />
                                      Créer un compte rendu
                                    </Button>

                                    {/* Liste des comptes rendus de ce chantier */}
                                    <p className="text-sm font-medium text-slate-600 mb-2">Comptes rendus de passage :</p>
                                    
                                    {clientFile.comptes_rendus?.filter(cr => cr.chantier_id === chantier.id).length > 0 ? (
                                      <div className="space-y-2">
                                        {clientFile.comptes_rendus
                                          .filter(cr => cr.chantier_id === chantier.id)
                                          .map((cr) => (
                                            <div 
                                              key={cr.id}
                                              className="bg-white rounded-lg p-3 border border-slate-200 hover:border-orange-300 cursor-pointer transition-colors"
                                              onClick={() => setShowCompteRenduDetail(showCompteRenduDetail === cr.id ? null : cr.id)}
                                            >
                                              <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-2">
                                                  <ClipboardCheck size={16} className="text-orange-500 mt-0.5" />
                                                  <div>
                                                    <p className="font-medium text-sm text-slate-800">{cr.title}</p>
                                                    <p className="text-xs text-slate-500">{formatDateTime(cr.created_at)}</p>
                                                  </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  {cr.voice_transcription && (
                                                    <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                      <Mic size={10} /> IA
                                                    </span>
                                                  )}
                                                  {(cr.photos_before?.length > 0 || cr.photos_after?.length > 0) && (
                                                    <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                      <Camera size={10} /> Photos
                                                    </span>
                                                  )}
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                    cr.status === 'validated' ? 'bg-green-100 text-green-600' :
                                                    cr.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                                                    'bg-slate-100 text-slate-600'
                                                  }`}>
                                                    {cr.status === 'validated' ? 'Validé' : 
                                                     cr.status === 'sent' ? 'Envoyé' : 'Brouillon'}
                                                  </span>
                                                </div>
                                              </div>

                                              {/* Détail du compte rendu */}
                                              <AnimatePresence>
                                                {showCompteRenduDetail === cr.id && (
                                                  <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="mt-3 pt-3 border-t border-slate-100"
                                                  >
                                                    <p className="text-sm text-slate-700 whitespace-pre-line mb-3">
                                                      {cr.description}
                                                    </p>
                                                    
                                                    {/* Checklist */}
                                                    {cr.checklist?.length > 0 && (
                                                      <div className="mb-3">
                                                        <p className="text-xs font-medium text-slate-500 mb-1">Checklist :</p>
                                                        <div className="flex flex-wrap gap-1">
                                                          {cr.checklist.map((item, idx) => (
                                                            <span 
                                                              key={idx}
                                                              className={`text-xs px-2 py-0.5 rounded ${
                                                                item.checked 
                                                                  ? 'bg-green-100 text-green-700' 
                                                                  : 'bg-slate-100 text-slate-500'
                                                              }`}
                                                            >
                                                              {item.checked ? '✓' : '○'} {item.label}
                                                            </span>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    )}
                                                    
                                                    <p className="text-xs text-slate-400">
                                                      Par {cr.created_by_name || 'Équipe'}
                                                    </p>
                                                  </motion.div>
                                                )}
                                              </AnimatePresence>
                                            </div>
                                          ))}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-slate-400 italic">Aucun compte rendu pour ce chantier</p>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <HardHat size={48} className="text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 mb-2">Aucun chantier pour ce client</p>
                        <p className="text-sm text-slate-400">
                          Les chantiers sont créés automatiquement lorsqu'un devis est signé
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Factures */}
                {activeTab === 'factures' && (
                  <div className="space-y-3">
                    {clientFile.factures?.map((facture) => (
                      <div key={facture.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-3">
                          <Receipt className="text-green-600" />
                          <div>
                            <p className="font-medium">{facture.facture_number}</p>
                            <p className="text-sm text-slate-500">{facture.subject}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(facture.total)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            facture.status === 'paid' ? 'bg-green-100 text-green-600' : 
                            'bg-amber-100 text-amber-600'
                          }`}>
                            {facture.status === 'paid' ? 'Payée' : 'En attente'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {(!clientFile.factures || clientFile.factures.length === 0) && (
                      <p className="text-center text-slate-400 py-8">Aucune facture</p>
                    )}
                  </div>
                )}

                {/* Échanges */}
                {activeTab === 'echanges' && (
                  <div className="space-y-3">
                    <div className="flex justify-end mb-4">
                      <Button size="sm" onClick={() => setShowAddExchange(true)}>
                        <Plus size={14} className="mr-1" /> Ajouter un échange
                      </Button>
                    </div>

                    {[...clientFile.emails || [], ...clientFile.messages || [], ...clientFile.notes || []]
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((exchange) => (
                        <div key={exchange.id} className={`p-4 rounded-lg border ${
                          exchange.direction === 'incoming' 
                            ? 'bg-blue-50 border-blue-200 ml-8' 
                            : 'bg-white border-slate-200 mr-8'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              {exchange.type?.includes('email') && <Mail size={14} className="text-blue-500" />}
                              {exchange.type === 'message' && <MessageSquare size={14} className="text-purple-500" />}
                              {exchange.type === 'note' && <StickyNote size={14} className="text-amber-500" />}
                              {exchange.type === 'call' && <PhoneCall size={14} className="text-cyan-500" />}
                              <span className="text-xs text-slate-500">
                                {exchange.direction === 'incoming' ? '← Reçu' : '→ Envoyé'}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400">{formatDateTime(exchange.created_at)}</span>
                          </div>
                          {exchange.subject && (
                            <p className="font-medium text-slate-800 mt-2">{exchange.subject}</p>
                          )}
                          <p className="text-sm text-slate-600 mt-1">{exchange.content}</p>
                          {exchange.created_by_name && (
                            <p className="text-xs text-slate-400 mt-2">Par {exchange.created_by_name}</p>
                          )}
                        </div>
                      ))}

                    {[...(clientFile.emails || []), ...(clientFile.messages || []), ...(clientFile.notes || [])].length === 0 && (
                      <p className="text-center text-slate-400 py-8">Aucun échange</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-slate-500">Client non trouvé</p>
            </div>
          )}
        </motion.div>

        {/* Modal Ajout Échange */}
        <AnimatePresence>
          {showAddExchange && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
              onClick={() => setShowAddExchange(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">Ajouter un échange</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={newExchange.type}
                      onChange={(e) => setNewExchange({ ...newExchange, type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    >
                      <option value="note">Note interne</option>
                      <option value="email_sent">Email envoyé</option>
                      <option value="email_received">Email reçu</option>
                      <option value="call">Appel téléphonique</option>
                      <option value="message">Message</option>
                    </select>
                  </div>

                  {(newExchange.type.includes('email')) && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Sujet</label>
                      <input
                        type="text"
                        value={newExchange.subject}
                        onChange={(e) => setNewExchange({ ...newExchange, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="Objet de l'email"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-1">Contenu</label>
                    <textarea
                      value={newExchange.content}
                      onChange={(e) => setNewExchange({ ...newExchange, content: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      rows={4}
                      placeholder="Détails de l'échange..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setShowAddExchange(false)}>Annuler</Button>
                  <Button onClick={handleAddExchange} disabled={sendingMessage}>
                    {sendingMessage ? 'Envoi...' : 'Ajouter'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* ════════════════════════════════════════════════════════════════ */}
        {/* MODAL AVERTISSEMENT LÉGAL - ENREGISTREMENT */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <AnimatePresence>
          {showLegalWarning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-[75] flex items-center justify-center p-4"
              onClick={() => setShowLegalWarning(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="legal-warning-modal"
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Enregistrement de l'appel</h3>
                    <p className="text-sm text-gray-500">Information légale importante</p>
                  </div>
                </div>
                
                {/* Message légal */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-2">
                        Cet appel sera enregistré
                      </p>
                      <p className="text-amber-700">
                        Conformément à la réglementation RGPD, tu dois <strong>informer ton interlocuteur</strong> au début de l'appel que la conversation est enregistrée à des fins de compte-rendu.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Phrase suggérée */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Phrase à dire :</p>
                  <p className="text-gray-700 italic">
                    "Je vous informe que cet appel est enregistré pour faciliter le suivi de notre échange. Êtes-vous d'accord ?"
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowLegalWarning(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    className="flex-1 bg-[#1E3A5F] hover:bg-[#2d5a8a]"
                    onClick={confirmAndStartCall}
                    data-testid="confirm-call-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    J'ai compris, lancer l'appel
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* ════════════════════════════════════════════════════════════════ */}
        {/* MODAL APPEL + ENREGISTREMENT */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <AnimatePresence>
          {showCallModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
              onClick={() => {}}
            >
              <motion.div
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 w-full max-w-md text-white shadow-2xl"
              >
                {/* État: Connexion en cours */}
                {callState === 'calling' && (
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-cyan-500/20 flex items-center justify-center animate-pulse">
                      <Phone size={40} className="text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Connexion en cours...</h3>
                    <p className="text-slate-400">{clientFile?.name}</p>
                    <p className="text-slate-500 text-sm mt-1">{clientFile?.phone}</p>
                    
                    <Button 
                      variant="destructive" 
                      className="mt-8 px-8"
                      onClick={() => {
                        setShowCallModal(false);
                        setCallState('idle');
                      }}
                    >
                      <PhoneOff size={18} className="mr-2" />
                      Annuler
                    </Button>
                  </div>
                )}
                
                {/* État: En appel (enregistrement) */}
                {callState === 'recording' && (
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center relative">
                      <Phone size={40} className="text-green-400" />
                      {isRecording && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold mb-1">Appel en cours</h3>
                    <p className="text-slate-400 mb-4">{clientFile?.name}</p>
                    
                    {/* Timer */}
                    <div className="text-4xl font-mono font-bold text-cyan-400 mb-6">
                      {formatDuration(callDuration)}
                    </div>
                    
                    {/* Indicateur enregistrement */}
                    {isRecording && (
                      <div className="flex items-center justify-center gap-2 text-red-400 mb-6">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        <span className="text-sm">Enregistrement en cours</span>
                        <Mic size={16} />
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex justify-center gap-4">
                      <button 
                        className="p-4 bg-slate-700 rounded-full hover:bg-slate-600 transition-colors"
                        onClick={() => setIsRecording(!isRecording)}
                      >
                        {isRecording ? <MicOff size={24} /> : <Mic size={24} />}
                      </button>
                      
                      <button 
                        className="p-4 bg-red-600 rounded-full hover:bg-red-500 transition-colors"
                        onClick={() => endCall(true)}
                        data-testid="end-call-btn"
                      >
                        <PhoneOff size={24} />
                      </button>
                    </div>
                    
                    <p className="text-xs text-slate-500 mt-4">
                      L'appel sera transcrit et résumé par IA
                    </p>
                  </div>
                )}
                
                {/* État: Traitement IA */}
                {callState === 'processing' && (
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Sparkles size={40} className="text-purple-400 animate-pulse" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Génération du compte-rendu...</h3>
                    <p className="text-slate-400 mb-4">L'IA analyse la conversation</p>
                    
                    <div className="space-y-2 text-sm text-slate-400">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Transcription audio...</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Analyse avec Claude Sonnet 4.6...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* État: Terminé */}
                {callState === 'done' && (
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle size={40} className="text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Appel terminé</h3>
                    <p className="text-slate-400 mb-2">Durée : {formatDuration(callDuration)}</p>
                    
                    {callSummary && (
                      <div className="bg-slate-700/50 rounded-xl p-4 mt-4 text-left text-sm max-h-60 overflow-y-auto">
                        <div className="flex items-center gap-2 text-cyan-400 font-semibold mb-2">
                          <FileAudio size={16} />
                          Compte-rendu IA
                        </div>
                        <p className="text-slate-300 whitespace-pre-wrap">{callSummary.summary}</p>
                      </div>
                    )}
                    
                    <div className="flex justify-center gap-3 mt-6">
                      <Button 
                        variant="outline" 
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        onClick={() => {
                          setShowCallModal(false);
                          setCallState('idle');
                          setCallSummary(null);
                        }}
                      >
                        Fermer
                      </Button>
                      {callSummary && (
                        <Button 
                          className="bg-cyan-600 hover:bg-cyan-500"
                          onClick={() => {
                            navigator.clipboard.writeText(callSummary.summary);
                            toast.success('Compte-rendu copié !');
                          }}
                        >
                          <Copy size={14} className="mr-1" />
                          Copier
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
